import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFilePdf,
  FaHistory,
  FaCalendarAlt,
  FaClipboardList,
  FaChevronDown,
  FaCodeBranch,
  FaLock,
  FaExclamationTriangle,
} from "react-icons/fa";

import PDFViewer from "../../components/fileViewers/PDFViewer/PDFViewer";
import ReusableModal from "../../components/ReusableModal";
import {
  getPdfByDealId,
  getDealPmStatus,
  getDealPmHistory,
} from "../../api/engagementApi";

import {
  ENGAGEMENT_BOXES,
  findCategory,
  summarizeBox,
  getInitials,
  colorFromString,
  resolveStatusBadge,
  extractPmStatsForSummary,
  getChangeRequests,
} from "./engagementUtils";

/* ────────────────────────── PM Status (per-deal) ────────────────────────── */
/**
 * Fetches PM status info for a deal. Reports stats up via `onStats` so the
 * parent page can compute aggregates (PM Updates total, Last Activity, …).
 */
const usePmStatus = (dealId, onStats) => {
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setParsed(null);
    (async () => {
      try {
        const data = await getDealPmStatus(dealId);
        if (cancelled) return;
        setParsed(data || null);
        if (onStats) onStats(dealId, extractPmStatsForSummary(data));
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, onStats]);

  return { loading, parsed, error };
};

/* ────────────────────────── PM History modal body ──────────────────────── */

const PmHistoryModalContent = ({ dealId, isDark }) => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getDealPmHistory(dealId);
        if (!cancelled) setEntries(data?.entries ?? []);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#eeeef6";
  const mutedColor = isDark ? "rgba(255,255,255,0.4)" : "#7070a0";
  const textColor = isDark ? "#e0e0f0" : "#1a1a2e";

  if (loading)
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "24px 0" }}>
        <div
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            border: "2px solid rgba(99,102,241,0.2)",
            borderTopColor: "#6366f1",
            animation: "spin 0.7s linear infinite",
          }}
        />
        <span style={{ fontSize: 13, color: mutedColor }}>Loading history…</span>
      </div>
    );

  if (error)
    return <p style={{ fontSize: 13, color: "#ef4444", padding: "16px 0" }}>Failed to load PM history.</p>;

  if (!entries.length)
    return <p style={{ fontSize: 13, color: mutedColor, padding: "16px 0" }}>No history available.</p>;

  return (
    <div style={{ padding: "4px 0" }}>
      {entries.map((entry, idx) => (
        <div
          key={idx}
          style={{
            borderLeft: `3px solid ${idx === 0 ? "#6366f1" : borderColor}`,
            paddingLeft: 14,
            paddingBottom: idx !== entries.length - 1 ? 20 : 0,
            marginBottom: idx !== entries.length - 1 ? 4 : 0,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: -7,
              top: 2,
              width: 11,
              height: 11,
              borderRadius: "50%",
              background: idx === 0 ? "#6366f1" : isDark ? "#444" : "#d0d0e8",
              border: `2px solid ${isDark ? "#1a1a2e" : "#fff"}`,
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
            <FaCalendarAlt size={10} color="#6366f1" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#6366f1",
                background: isDark ? "rgba(99,102,241,0.12)" : "#ededfc",
                borderRadius: 4,
                padding: "1px 8px",
              }}
            >
              {entry.date}
            </span>
            {idx === 0 && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#22c55e",
                  background: isDark ? "rgba(34,197,94,0.12)" : "#dcfce7",
                  borderRadius: 4,
                  padding: "1px 6px",
                }}
              >
                Latest
              </span>
            )}
          </div>
          <p
            style={{
              fontSize: 13,
              lineHeight: 1.6,
              color: idx === 0 ? textColor : mutedColor,
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {entry.content}
          </p>
        </div>
      ))}
    </div>
  );
};

/* ────────────────────────── Key/value list ────────────────────────── */

const KeyValueList = ({ items }) => {
  if (!items || !items.length) {
    return (
      <div style={{ fontSize: 12.5, color: "var(--color-text-light)", fontStyle: "italic" }}>
        No data available for this section.
      </div>
    );
  }
  return (
    <div>
      {items.map((it, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            padding: "8px 0",
            borderBottom:
              idx === items.length - 1 ? "none" : "1px solid var(--color-border)",
          }}
        >
          <span
            style={{
              minWidth: 150,
              flexShrink: 0,
              fontSize: 12.5,
              fontWeight: 500,
              color: "var(--color-text-light)",
            }}
          >
            {it.name || "—"}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 180,
              fontSize: 13,
              fontWeight: 600,
              color: "var(--color-text)",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {it.value ?? "N/A"}
            {it.low_confidence && (
              <span
                title="Low-confidence extraction — verify against source PDF"
                style={{ color: "var(--color-warning, #b45309)", display: "inline-flex" }}
              >
                <FaExclamationTriangle size={11} />
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ────────────────────────── Collapsible section row ────────────────────────── */

const CollapsibleSection = ({ icon: Icon, accent, label, count, unit, open, onToggle, children }) => (
  <div
    style={{
      border: "1px solid var(--color-border)",
      borderRadius: 10,
      background: "var(--color-bg)",
      marginBottom: 8,
      overflow: "hidden",
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      style={{
        all: "unset",
        boxSizing: "border-box",
        width: "100%",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 14px",
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: `${accent}1f`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={14} color={accent} />
      </span>
      <span
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          color: "var(--color-text)",
          flex: 1,
          minWidth: 0,
        }}
      >
        {label}
      </span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: 0.2 }}
        style={{ display: "flex", color: "var(--color-text-light)" }}
      >
        <FaChevronDown size={12} />
      </motion.span>
    </button>

    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22 }}
          style={{ overflow: "hidden" }}
        >
          <div style={{ padding: "4px 14px 14px" }}>{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ────────────────────────── Change Requests section ────────────────────────── */

const ChangeRequestRow = ({ cr, isLast }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderBottom: isLast ? "none" : "1px solid var(--color-border)",
        padding: "10px 0",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          all: "unset",
          boxSizing: "border-box",
          width: "100%",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 13,
            fontWeight: 600,
            color: "var(--color-text)",
            flex: 1,
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          {cr.name}
          {cr.is_original && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#16a34a",
                background: "rgba(34,197,94,0.15)",
                borderRadius: 4,
                padding: "1px 6px",
              }}
            >
              Original SOW
            </span>
          )}
        </span>
        {cr.date && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--color-primary)",
              whiteSpace: "nowrap",
            }}
          >
            {cr.date}
          </span>
        )}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ display: "flex", color: "var(--color-text-light)" }}
        >
          <FaChevronDown size={11} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ paddingTop: 10 }}>
              {cr.pm_status && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: "var(--color-text)",
                    background: "var(--color-bg-card)",
                    border: "1px solid var(--color-border)",
                    borderLeft: "3px solid var(--color-primary)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    marginBottom: 10,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {cr.pm_status}
                </div>
              )}
              {(cr.categories || []).length > 0 ? (
                cr.categories.map((cat, ci) => (
                  <div key={ci} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "var(--color-text-light)",
                        marginBottom: 4,
                      }}
                    >
                      {cat.category}
                    </div>
                    <KeyValueList items={cat.items} />
                  </div>
                ))
              ) : (
                !cr.pm_status && (
                  <div style={{ fontSize: 12.5, color: "var(--color-text-light)", fontStyle: "italic" }}>
                    No additional details for this change request.
                  </div>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ────────────────────────── PM Status footer ────────────────────────── */

const PmStatusFooter = ({ parsed, loading, error, onViewAll }) => {
  if (loading)
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            border: "2px solid rgba(99,102,241,0.2)",
            borderTopColor: "#6366f1",
            animation: "spin 0.7s linear infinite",
          }}
        />
        <span style={{ fontSize: 12, color: "var(--color-text-light)" }}>Loading PM status…</span>
      </div>
    );

  if (error)
    return (
      <span style={{ fontSize: 12, color: "#ef4444", marginTop: 14, display: "inline-block" }}>
        Failed to load status
      </span>
    );

  const totalEntries = parsed?.totalEntries || (parsed?.hasDate ? 1 : 0);
  const content = parsed?.content && parsed.content !== "N/A" ? parsed.content : null;

  return (
    <div
      style={{
        marginTop: 14,
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 110, display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 700,
            color: "var(--color-text-light)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          <FaClipboardList size={11} color="var(--color-text-light)" /> PM Status
          {totalEntries > 0 && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 18,
                height: 18,
                padding: "0 6px",
                background: "var(--color-primary-light)",
                color: "var(--color-primary)",
                borderRadius: 9,
                fontSize: 10.5,
                fontWeight: 700,
              }}
            >
              {totalEntries}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-light)" }}>UPDATES</div>
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 220,
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: 10,
          padding: "10px 12px",
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        {parsed?.date && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              minWidth: 64,
            }}
          >
            <span
              style={{
                color: "var(--color-primary)",
                fontWeight: 700,
                fontSize: 13,
                whiteSpace: "nowrap",
              }}
            >
              {parsed.date}
            </span>
            {totalEntries > 1 && (
              <button
                type="button"
                onClick={onViewAll}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  marginTop: 2,
                  cursor: "pointer",
                  color: "var(--color-primary)",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                View all ›
              </button>
            )}
          </div>
        )}
        <div
          style={{
            fontSize: 12.5,
            color: "var(--color-text)",
            lineHeight: 1.5,
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
            minWidth: 0,
          }}
        >
          {content || (
            <span style={{ color: "var(--color-text-light)", fontStyle: "italic" }}>
              No recent update
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────── Single deal card ────────────────────────── */

const DealCard = ({ deal, index, onPmStats, onOpenHistory, onOpenPdf }) => {
  const handleStats = useCallback(
    (id, info) => {
      if (onPmStats) onPmStats(id, info);
    },
    [onPmStats]
  );

  const { loading, parsed, error } = usePmStatus(deal.deal_id, handleStats);

  const status = resolveStatusBadge(deal);
  const totalEntries = parsed?.totalEntries || (parsed?.hasDate ? 1 : 0);

  const pmoEmail = deal.assigned_to || null;
  const initials = getInitials(pmoEmail || deal.deal_name);
  const avatarColor = colorFromString(pmoEmail || deal.deal_name || "");

  const changeRequests = getChangeRequests(deal);

  // Whole-card collapse: lower details are hidden by default and slide open
  // when the SOW header row (or its chevron) is clicked.
  const [cardOpen, setCardOpen] = useState(false);

  // Tracks which collapsible sections are open (keyed by box key / "crs").
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const cardAnim = {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      variants={cardAnim}
      initial="initial"
      animate="animate"
      transition={{ delay: index * 0.06 }}
      style={{
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: "var(--shadow-sm)",
        borderLeft: `4px solid ${status.color}`,
      }}
    >
      {/* ── Header row: title + service areas + status + chevron (whole row toggles the card) ── */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={cardOpen}
        onClick={() => setCardOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setCardOpen((o) => !o);
          }
        }}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: cardOpen ? 16 : 0,
          cursor: "pointer",
          userSelect: "none",
          transition: "margin-bottom 0.2s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          <h5
            title={deal.deal_name}
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "var(--color-text)",
              margin: 0,
              wordBreak: "break-word",
            }}
          >
            {deal.deal_name}
          </h5>

          {/* Service areas moved up onto the header line */}
          {deal.service_areas?.map((s, k) => (
            <span
              key={k}
              style={{
                fontSize: 12,
                padding: "3px 10px",
                borderRadius: 999,
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-light)",
                fontWeight: 500,
              }}
            >
              {s}
            </span>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: status.bg,
              color: status.color,
              fontWeight: 600,
              fontSize: 12,
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: status.color,
              }}
            />
            {status.label}
          </span>

          <motion.span
            animate={{ rotate: cardOpen ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              borderRadius: 8,
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-light)",
              flexShrink: 0,
            }}
          >
            <FaChevronDown size={12} />
          </motion.span>
        </div>
      </div>

      {/* ── Collapsible lower section (kept mounted to preserve child state & PM fetch) ── */}
      <motion.div
        initial={false}
        animate={{ height: cardOpen ? "auto" : 0, opacity: cardOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        {/* ── Collapsible sections (replaces the 6 tile boxes) ── */}
        <div style={{ marginBottom: 14 }}>
          {ENGAGEMENT_BOXES.map((box) => {
            const category = findCategory(deal.categories, box);
            const { value } = summarizeBox(category, box);
            const items = category?.items || [];
            return (
              <CollapsibleSection
                key={box.key}
                icon={box.icon}
                accent={box.accent}
                label={box.label}
                count={value}
                unit={box.unit}
                open={!!openSections[box.key]}
                onToggle={() => toggleSection(box.key)}
              >
                <KeyValueList items={items} />
              </CollapsibleSection>
            );
          })}

          {/* ── Change Requests collapsible section (Option A) ── */}
          <CollapsibleSection
            icon={FaCodeBranch}
            accent="#0ea5e9"
            label="Change Requests"
            count={changeRequests.length}
            unit={changeRequests.length === 1 ? "CR" : "CRs"}
            open={!!openSections.crs}
            onToggle={() => toggleSection("crs")}
          >
            {changeRequests.length ? (
              <div>
                {changeRequests.map((cr, ci) => (
                  <ChangeRequestRow
                    key={cr.id}
                    cr={cr}
                    isLast={ci === changeRequests.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: "var(--color-text-light)", fontStyle: "italic" }}>
                No change requests linked to this SOW yet.
              </div>
            )}
          </CollapsibleSection>
        </div>

        {/* ── PMO row ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 4,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: avatarColor,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: "var(--color-text-light)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              PMO
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--color-text)",
                wordBreak: "break-word",
              }}
            >
              {pmoEmail || "—"}
            </div>
          </div>

          {/* PDF button on right */}
          {deal.has_pdf_path && deal.pdf_access_restricted && (
            <button
              type="button"
              disabled
              title="View SOW — restricted to Sales"
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                borderRadius: 8,
                background: "var(--color-border)",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-light)",
                fontWeight: 600,
                fontSize: 12,
                cursor: "not-allowed",
              }}
            >
              <FaLock size={12} /> View SOW
            </button>
          )}
          {deal.has_pdf_path && !deal.pdf_access_restricted && (
            <button
              type="button"
              onClick={() => onOpenPdf(deal)}
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                borderRadius: 8,
                background: "var(--color-primary-light)",
                border: "1px solid rgba(114,105,239,0.3)",
                color: "var(--color-primary)",
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              <FaFilePdf size={12} /> View SOW
            </button>
          )}
        </div>

        {/* ── PM Status footer ── */}
        <PmStatusFooter
          parsed={parsed}
          loading={loading}
          error={error}
          onViewAll={() => onOpenHistory(deal.deal_id, totalEntries)}
        />
      </motion.div>
    </motion.div>
  );
};

/* ────────────────────────── Main component ────────────────────────── */

export default function EngagementCards({ deals, onPmStats }) {
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" && document.body.classList.contains("dark-mode")
  );
  const [pdfModal, setPdfModal] = useState({ open: false, dealId: null });
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [historyModal, setHistoryModal] = useState({
    open: false,
    dealId: null,
    totalEntries: 0,
  });

  useEffect(() => {
    const check = () => setIsDark(document.body.classList.contains("dark-mode"));
    const obs = new MutationObserver(check);
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("themechange", check);
    return () => {
      obs.disconnect();
      window.removeEventListener("themechange", check);
    };
  }, []);

  const openPdfModal = async (deal) => {
    setPdfModal({ open: true, dealId: deal.deal_id });
    setPdfUrl(null);
    setPdfLoading(true);
    try {
      const url = await getPdfByDealId(deal.deal_id);
      setPdfUrl(url);
    } catch (err) {
      console.error("Failed to fetch PDF path:", err);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfModal = () => {
    setPdfModal({ open: false, dealId: null });
    setPdfUrl(null);
  };

  const openHistoryModal = (dealId, totalEntries) =>
    setHistoryModal({ open: true, dealId, totalEntries });
  const closeHistoryModal = () => setHistoryModal({ open: false, dealId: null, totalEntries: 0 });

  return (
    <>
      <ReusableModal
        open={pdfModal.open}
        onClose={closePdfModal}
        title="SOW Document"
        icon={FaFilePdf}
        width="min(95vw, 1000px)"
        maxHeight="90vh"
      >
        <div style={{ padding: 24 }}>
          {pdfLoading && <p style={{ color: "var(--color-text-light)" }}>Loading PDF...</p>}
          {!pdfLoading && pdfUrl && <PDFViewer pdfUrl={pdfUrl} />}
          {!pdfLoading && !pdfUrl && (
            <p style={{ color: "var(--color-text-light)" }}>No PDF available.</p>
          )}
        </div>
      </ReusableModal>

      <ReusableModal
        open={historyModal.open}
        onClose={closeHistoryModal}
        title={`PM Status History${
          historyModal.totalEntries ? ` · ${historyModal.totalEntries} entries` : ""
        }`}
        icon={FaHistory}
        width="min(95vw, 640px)"
        maxHeight="80vh"
      >
        <div style={{ padding: "8px 24px 24px" }}>
          {historyModal.open && historyModal.dealId && (
            <PmHistoryModalContent dealId={historyModal.dealId} isDark={isDark} />
          )}
        </div>
      </ReusableModal>

      <div>
        {deals.map((deal, i) => (
          <DealCard
            key={deal.deal_id ?? i}
            deal={deal}
            index={i}
            onPmStats={onPmStats}
            onOpenHistory={openHistoryModal}
            onOpenPdf={openPdfModal}
          />
        ))}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
